<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>
                    <slot name="title"></slot>
                </h4>
            </div>
            <a
                data-action="collapse"
                :class="{ rotate: isCollapsed }"
                @click="triggerCollapse"
                class="mr-2"
            >
                <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                />
            </a>
        </div>
        <div class="card-body" v-if="noData" style="text-align:center;">
            {{ $t("org.message.No Data") }}
        </div>
        <div
            v-else
            :ref="refName[field]"
        >
            <VsSvgPie
                v-if="width > 0"
                :colorScale="colorScale"
                :data="chartData"
                :labelName="labelName"
                :valueName="valueName"
                :width="width"
                :height="height"
                :radiusIn="0.7"
                :animation="false"
                :hoverBright="true"
                @click:point="({ event, data }) => handleLabelClick(data.label)"
            >
                <template v-slot:path-label="{ item }">
                    <template v-if="item.index < labelMaxLength">
                        {{ item.label }}
                    </template>
                </template>
            </VsSvgPie>
        </div>
        <b-collapse
            v-model="visible"
            :visible="visible"
            class="card-content"
        >
            <b-table-simple
                sticky-header
            >
                <b-tbody>
                    <b-tr v-for="(item, index) in finalData.sort(sorter)" :key="index">
                        <b-td style="word-break:break-all;">
                            <b-form-checkbox
                                v-model="checked"
                                switch
                                inline
                                :value="item.key"
                                @change="changeSwitch"
                            >
                                <span class="switch-icon-left">
                                    <feather-icon
                                        icon="CheckIcon"/>
                                </span>
                                <label @click="handleLabelClick(item.key)">
                                    {{ item.key }}
                                </label>
                            </b-form-checkbox>
                        </b-td>
                        <b-td class="text-right" style="width:30%;">
                            {{ filterDataMap[item.key] ? $_getDollar(filterDataMap[item.key]): undefined }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-collapse>
    </b-card>
</template>

<script>
import { getCurrentInstance, computed, ref, toRefs, onUpdated, onMounted, onUnmounted, watch } from '@vue/composition-api'
import { VsSvgPie } from '@/libs/vuesign/chart'
export default {
    components: {
        VsSvgPie
    },
    props: {
        field: {
            type: String
        },
        fullData: {
            type: Array,
            default: function () {
                return []
            }
        },
        filterData: {
            type: Array,
            default: function () {
                return []
            }
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        }
    },
    setup (props, context) {
        const { themeColors, filterData, fullData, field } = toRefs(props)
        const vm = getCurrentInstance().proxy
        const d3 = vm.$d3
        const width = ref(0)
        const height = 250
        const labelName = 'key'
        const valueName = 'cost'
        const labelMaxLength = 3
        const visible = ref(false)
        const isCollapsed = ref(false)
        const checked = ref(filterData.value.slice().map(item => item.key))
        const sorter = (a, b) => {
            if (a.cost < b.cost || (typeof a.cost === 'undefined' && typeof b.cost === 'number')) {
                return 1
            } else if (typeof b.cost === 'undefined' && checked.value.includes(b.key)) {
                return 1
            } else if (typeof b.cost === 'undefined') {
                return -1
            }
            return 0
        }
        const filterDataMap = computed(() => {
            return filterData.value.reduce((accumlator, currentValue) => {
                accumlator[currentValue.key] = currentValue.cost
                return accumlator
            }, {})
        })
        const finalData = computed(() => {
            return fullData.value.map(item => {
                return {
                    ...item,
                    cost: filterDataMap.value[item.key]
                }
            })
        })
        const chartData = computed(() => filterData.value.filter(item => item.cost > 0))
        const colorScale = computed(() => d3.scaleOrdinal().domain(chartData.value.map(item => item.cost)).range(themeColors.value.range.slice().reverse()))
        const noData = computed(() => !fullData.value.length)

        const handleLabelClick = (value) => {
            if (checked.value.length === 1 && checked.value[0] === value) {
                checked.value = finalData.value.map(item => item.key)
            } else {
                checked.value = [value]
            }
            context.emit('filter:items', { field: field.value, value: checked.value })
        }
        const changeSwitch = (value) => {
            checked.value = value.length ? value : fullData.value.map(v => v.key)
            context.emit('filter:items', { field: field.value, value: checked.value })
        }
        const triggerCollapse = () => {
            visible.value = !visible.value
            isCollapsed.value = !isCollapsed.value
        }
        // resize
        const refName = {
            productCode: 'REF_DYNAMICSEARCH_PRODUCT_PIE',
            productCategory: 'REF_DYNAMICSEARCH_CATEGORY_PIE'
        }
        const refElemnet = {}
        refElemnet[refName[field.value]] = ref(null)
        const resize = () => {
            if (refElemnet[refName[field.value]].value) {
                width.value = refElemnet[refName[field.value]].value.getBoundingClientRect().width
            }
        }
        onUpdated(resize)
        onMounted(() => {
            vm.$nextTick(() => {
                resize()
                window.addEventListener('resize', resize)
            })
        })
        onUnmounted(() => {
            window.removeEventListener('resize', resize)
        })
        // watch
        watch(fullData, (value, oldValue) => {
            checked.value = value.map(v => v.key)
        }, {
            deep: true
        })
        return {
            refElemnet,
            ...refElemnet,
            refName,
            width,
            height,
            labelName,
            valueName,
            labelMaxLength,
            visible,
            isCollapsed,
            colorScale,
            noData,
            checked,
            filterDataMap,
            chartData,
            finalData,
            triggerCollapse,
            handleLabelClick,
            changeSwitch,
            sorter
        }
    }
}
</script>

<style>

</style>
